import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Share2 } from 'lucide-react';
import SpotArticle from '../../components/blog/SpotArticle';
import BlogList from '../../components/blog/BlogList';
import { Button } from '../../components/ui/button';
import { getSpotBySlug, getRecentSpots } from '../../data/spots/articles';

const SpotArticlePage = () => {
  const { slug } = useParams();
  const spot = getSpotBySlug(slug);
  const recentSpots = getRecentSpots(3).filter(s => s.slug !== slug);

  // Si le spot n'existe pas, rediriger vers 404
  if (!spot) {
    return <Navigate to="/404" replace />;
  }

  const shareUrl = `https://sun-spot.app/blog/spots/${spot.slug}`;
  
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: spot.title,
          text: spot.description,
          url: shareUrl,
        });
      } catch (err) {
        console.log('Partage annulé');
      }
    } else {
      // Fallback: copier l'URL dans le presse-papiers
      navigator.clipboard.writeText(shareUrl);
      // Vous pourriez ajouter un toast ici pour confirmer la copie
    }
  };

  return (
    <>
      <Helmet>
        <title>{spot.title} - Guide Complet | Sun-Spot</title>
        <meta name="description" content={spot.description} />
        <meta name="keywords" content={spot.seoKeywords.join(', ')} />
        <link rel="canonical" href={`https://sun-spot.app/blog/spots/${spot.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={`${spot.title} - Guide Complet`} />
        <meta property="og:description" content={spot.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://sun-spot.app/blog/spots/${spot.slug}`} />
        <meta property="og:image" content={spot.images[0]?.src ? `https://sun-spot.app${spot.images[0].src}` : "https://sun-spot.app/android-chrome-512x512.png"} />
        <meta property="og:image:width" content={spot.images[0]?.width || "1200"} />
        <meta property="og:image:height" content={spot.images[0]?.height || "800"} />
        <meta property="og:image:alt" content={spot.images[0]?.alt || spot.title} />
        <meta property="article:published_time" content={spot.publishedAt} />
        <meta property="article:modified_time" content={spot.updatedAt} />
        <meta property="article:author" content={spot.author} />
        <meta property="article:section" content="Spots Panoramiques" />
        {spot.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${spot.title} - Guide Complet`} />
        <meta name="twitter:description" content={spot.description} />
        <meta name="twitter:image" content={spot.images[0]?.src ? `https://sun-spot.app${spot.images[0].src}` : "https://sun-spot.app/android-chrome-512x512.png"} />
        <meta name="twitter:image:alt" content={spot.images[0]?.alt || spot.title} />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": spot.title,
            "description": spot.description,
            "url": `https://sun-spot.app/blog/spots/${spot.slug}`,
            "datePublished": spot.publishedAt,
            "dateModified": spot.updatedAt,
            "author": {
              "@type": "Organization",
              "name": spot.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "Sun-Spot",
              "url": "https://sun-spot.app",
              "logo": {
                "@type": "ImageObject",
                "url": "https://sun-spot.app/logo-192.png"
              }
            },
            "image": spot.images.map(img => ({
              "@type": "ImageObject",
              "url": `https://sun-spot.app${img.src}`,
              "width": img.width,
              "height": img.height,
              "caption": img.alt
            })),
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://sun-spot.app/blog/spots/${spot.slug}`
            },
            "keywords": spot.seoKeywords.join(', '),
            "about": {
              "@type": "Place",
              "name": spot.location,
              "geo": {
                "@type": "GeoCoordinates",
                "latitude": spot.coordinates.lat,
                "longitude": spot.coordinates.lng
              }
            }
          })}
        </script>
      </Helmet>
      
      <div className="pt-16 min-h-screen bg-background">
        {/* Navigation */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" asChild className="hover:bg-amber-50">
              <Link to="/blog" className="flex items-center">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au carnet
              </Link>
            </Button>
            
            <Button variant="outline" onClick={handleShare} className="flex items-center">
              <Share2 className="w-4 h-4 mr-2" />
              Partager
            </Button>
          </div>
        </div>

        {/* Article principal */}
        <div className="px-4 sm:px-6 lg:px-8 pb-16">
          <SpotArticle spot={spot} isPreview={false} />
        </div>

        {/* Articles similaires */}
        {recentSpots.length > 0 && (
          <section className="bg-amber-50 py-16">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-amber-900 text-center mb-12">
                Autres Spots à Découvrir
              </h2>
              <BlogList spots={recentSpots} showTitle={false} />
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default SpotArticlePage;